import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AccountSwitcher from './AccountSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { useGuardian } from '../contexts/GuardianContext';
import { useSubscription } from '../contexts/SubscriptionContext';

const { FiHome, FiGrid, FiVideo, FiBookOpen, FiSettings, FiHelpCircle, FiMenu, FiX, FiLogOut, FiLock, FiMail } = FiIcons;

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isGuardian } = useGuardian();
  const { canAccessPremiumFeatures } = useSubscription();

  const hasAccess = canAccessPremiumFeatures();

  const navItems = [
    { path: '/', icon: FiHome, label: 'Home' },
    { path: '/dashboard', icon: FiGrid, label: 'Dashboard', protected: true, requiresAccess: false },
    { path: '/schools', icon: FiBookOpen, label: 'Schools', protected: true, requiresAccess: true },
    { path: '/communications', icon: FiMail, label: 'Outreach', protected: true, requiresAccess: true },
    { path: '/content-planning', icon: FiVideo, label: 'Content', protected: true, requiresAccess: true },
    { path: '/settings', icon: FiSettings, label: 'Settings', protected: true, requiresAccess: false },
    { path: '/faq', icon: FiHelpCircle, label: 'FAQ' },
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleNavClick = (item) => {
    if (item.requiresAccess && !hasAccess) {
      navigate('/settings?tab=subscription');
    } else {
      navigate(item.path);
    }
    setIsOpen(false);
  };

  const visibleNavItems = user ? navItems : navItems.filter((item) => !item.protected);

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xs sm:text-sm">NL</span>
            </div>
            <span className="font-space-grotesk font-bold text-base sm:text-xl text-secondary">
              The<span className="text-primary">N</span>ext<span className="text-primary">L</span>ete
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1 lg:space-x-4">
            {user && isGuardian && (
              <div className="mr-2 lg:mr-4">
                <AccountSwitcher />
              </div>
            )}
            {visibleNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavClick(item)}
                disabled={item.requiresAccess && !hasAccess}
                className={`flex items-center space-x-1 lg:space-x-2 px-2 lg:px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'text-primary bg-blue-50'
                    : item.requiresAccess && !hasAccess
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                }`}
              >
                <SafeIcon icon={item.icon} className="w-4 h-4" />
                <span className="hidden lg:inline">{item.label}</span>
                {item.requiresAccess && !hasAccess && <SafeIcon icon={FiLock} className="w-3 h-3" />}
              </button>
            ))}
            {user && (
              <button
                onClick={handleSignOut}
                className="flex items-center space-x-1 lg:space-x-2 px-2 lg:px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                <span className="hidden lg:inline">Sign Out</span>
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-600 hover:text-primary p-2">
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden border-t border-gray-200"
            >
              <div className="py-3">
                {user && isGuardian && (
                  <div className="px-4 mb-3">
                    <AccountSwitcher />
                  </div>
                )}
                {visibleNavItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item)}
                    disabled={item.requiresAccess && !hasAccess}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors ${
                      location.pathname === item.path
                        ? 'text-primary bg-blue-50'
                        : item.requiresAccess && !hasAccess
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={item.icon} className="w-5 h-5" />
                      <span>{item.label}</span>
                    </div>
                    {item.requiresAccess && !hasAccess && <SafeIcon icon={FiLock} className="w-4 h-4" />}
                  </button>
                ))}
                {user && (
                  <button
                    onClick={() => {
                      handleSignOut();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <SafeIcon icon={FiLogOut} className="w-5 h-5" />
                    <span>Sign Out</span>
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}

export default Navbar;